import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { CalendarIcon, MapPinIcon } from "../../../../assets/icons/interface/interface";
import { Button } from "../../../../components/ui/button";
import { Dialog, DialogContent, DialogHeader } from "../../../../components/ui/dialog";
import { cn } from "../../../../handlers/cn.utils";
import { JobApplicationState, JobApplicationStatus } from "../../../../handlers/enums";
import { formatAPIDate } from "../../../../handlers/utilityFunctions";
import JobStatusForm from "./JobStatusForm";

function ApplicationDetails({ application, refetchData, getStatusColor }) {
	const [dialogOpen, setDialogOpen] = useState(false);
	const [statusUpdateData, setStatusUpdateData] = useState(null);

	const stages = useMemo(() => {
		return application?.stages || [];
	}, [application]);

	return (
		<>
			<div className="max-w-6xl mx-auto px-3 sm:px-4 lg:px-6 py-8">
				{/* The Application Details Header*/}

				<div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6">
					<div className="flex items-center mb-4 sm:mb-0">
						<Link to={`/companies/${application?.companyId}`} state={application?.companyId}>
							<img
								alt={`${application.companyName || ""} logo`}
								loading="lazy"
								width="64"
								height="64"
								decoding="async"
								data-nimg="1"
								className="rounded-lg mr-4"
								style={{ color: "transparent" }}
								src={application.companyLogo ?? "/images/co-placeholder.png"}
								onError={(e) => (e.target.src = "/images/co-placeholder.png")}
							/>
						</Link>
						<div>
							<h1 className="text-2xl font-bold text-gray-900 font-plus-jakarta">{application?.jobTitle}</h1>
							<Link to={`/companies/${application?.companyId}`} state={application?.companyId}>
								<p className="text-lg text-gray-700 font-inter">{application?.companyName || ""}</p>
							</Link>
						</div>
					</div>
				</div>
				<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-4 mb-6">
					<div className="flex items-center">
						<MapPinIcon className="w-5 h-5 mr-2 text-gray-500" />
						<span className="text-gray-700 text-sm">Dar es Salaam, Dar es Salaam</span>
					</div>
					<div className="flex items-center">
						<CalendarIcon className="w-5 h-5 mr-2 text-gray-500" />
						<span className="text-gray-700 text-sm">
							Applied On {formatAPIDate("MMM D, YYYY", application?.createdAt)}
						</span>
					</div>
				</div>
				{/* status indicator */}
				{stages.length > 0 && (
					<div className="flex items-center gap-4 ">
						{stages.map((stage, index) => (
							<div
								key={index}
								className="w-full"
								onClick={() => {
									if (stage.status === JobApplicationStatus.APPLIED) return;
									setStatusUpdateData(stage);
									setDialogOpen(true);
								}}
							>
								<div
									className={cn("h-2 rounded-full mb-2 w-full bg-green-500", {
										"bg-green-500":
											index !== stages.length - 1 ||
											stage.state === JobApplicationState.COMPLETED ||
											stage.state === JobApplicationState.PASSED,
										"bg-yellow-500": index === stages.length - 1 && stage.state === JobApplicationState.SCHEDULED,
										"bg-gray-500": index === stages.length - 1 && stage.state === JobApplicationState.NOT_STARTED,
										"bg-blue-500": index === stages.length - 1 && stage.state === JobApplicationState.IN_PROGRESS,
										"bg-red-500":
											(index === stages.length - 1 && stage.state === JobApplicationState.FAILED) ||
											stage.state === JobApplicationState.REJECTED,
									})}
								/>
								<p className="text-xs font-semibold text-gray-700 capitalize">
									{stage.status.replace(/_/g, " ").toLowerCase()}
								</p>
								<p className="text-xs text-gray-600 mt-[0.10rem]">
									{stage.dueDate
										? `${stage.state}: ${formatAPIDate("MMM D, YYYY", stage.dueDate)}`
										: `Updated ${formatAPIDate("MMM D, YYYY", stage.updatedAt ? stage.updatedAt : "")}`}
								</p>
							</div>
						))}
					</div>
				)}
				<div className="flex items-center justify-end px-4">
					<Button size="sm" variant="secondary" className="shadow-none" onClick={() => setDialogOpen(true)}>
						Add Stage
					</Button>
				</div>
				{/* end status indicator */}
			</div>

			{/* Application Details Body*/}
			<div className="pt-5" style={{ borderTopWidth: "1px" }}>
				{application.docs?.length ? (
					<div>
						<h2 className="text-base font-semibold mb-2 font-inter">Application Attachments</h2>
						<div className="flex flex-wrap gap-2">
							{application.docs.map((doc, index) => (
								<span key={index} className="bg-gray-100 p-1 rounded text-xs">
									{`${doc.doc_sub_type} - ${doc.file_name}`}
								</span>
							))}
						</div>
					</div>
				) : null}

				<div className="mt-5">
					<h2 className="text-base font-semibold mb-2 font-inter">Status History</h2>
					<div className="space-y-2">
						{application.stages?.length ? (
							application.stages.map((stage, index) => (
								<div key={index} className="flex justify-between border-b pb-2">
									<span className={`px-2 py-1 rounded text-xs ${getStatusColor(stage.status)}`}>{stage.status}</span>
									<span className="text-gray-500 text-sm">
										{stage.createdAt ? formatAPIDate("MMM D, YYYY", stage.createdAt) : "No date"}
									</span>
									{stage.notes && <span className="text-sm">{stage.notes}</span>}
								</div>
							))
						) : (
							<p className="text-gray-500">No status updates available</p>
						)}
					</div>
				</div>
			</div>

			{/* Editing Dialog*/}
			<Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
				<DialogContent className="max-w-2xl">
					<DialogHeader className="font-medium">Update Job Stage</DialogHeader>
					<JobStatusForm
						applicationId={application?._id}
						onCancel={() => {
							setDialogOpen(false);
							setStatusUpdateData(null);
						}}
						onSuccess={() => {
							refetchData();
							setStatusUpdateData(null);
						}}
						currentStatus={statusUpdateData}
					/>
				</DialogContent>
			</Dialog>
		</>
	);
}

export default ApplicationDetails;
